/**
 * A chip in the suggested-filter row. Suggested filters are personalized per
 * retailer and ranked by expected usefulness, so the order of
 * `SUGGESTED_FILTERS` is itself part of the design.
 */
export type SuggestedFilter =
  /** Applies or clears a single value; no popup. */
  | {
      kind: "toggle";
      key: string;
      label: string;
      /** Chips that carry a leading glyph in production. */
      icon?: "insider" | "topShop";
    }
  /** Opens the min/max price popup. */
  | { kind: "price"; key: string; label: string }
  /** Opens a checkbox popup; several values may be applied at once. */
  | { kind: "multi"; key: string; label: string; options: FilterOption[] };

/**
 * Values for the Brand values chip. The All filters modal lists this section
 * as a placeholder, so the options originate here.
 */
export const BRAND_VALUE_OPTIONS: FilterOption[] = [
  { value: "asian-owned", label: "Asian owned" },
  { value: "black-owned", label: "Black owned" },
  { value: "deaf-owned", label: "Deaf owned" },
  { value: "women-owned", label: "Women owned" },
  { value: "lgbtq-owned", label: "LGBTQ+ owned" },
  { value: "latinx-owned", label: "Latinx owned" },
  { value: "veteran-owned", label: "Veteran owned" },
  { value: "handmade", label: "Handmade" },
  { value: "sustainable", label: "Sustainable" },
];

/** The modal lists three and hides the rest behind "Show more". */
const VISIBLE_BRAND_VALUE_COUNT = 3;

/**
 * Brand minimum bounds. Only the ceiling matters — "up to $150" — so the top
 * of the range means "any minimum" rather than a cap.
 */
export const BRAND_MINIMUM_RANGE_MIN = 0;
export const BRAND_MINIMUM_RANGE_MAX = 400;
export const BRAND_MINIMUM_STEP = 50;

/** Price popup bounds. The top of the range means "and up", not a hard cap. */
export const PRICE_RANGE_MIN = 0;
export const PRICE_RANGE_MAX = 100;
export const PRICE_STEP = 5;

/**
 * Ranked highest-intent first, standing in for the personalization signal that
 * would order these in production.
 */
export const SUGGESTED_FILTERS: SuggestedFilter[] = [
  { kind: "price", key: "price", label: "Price" },
  { kind: "toggle", key: "no-import-duties", label: "No import duties" },
  {
    kind: "multi",
    key: "brand-values",
    label: "Brand values",
    options: BRAND_VALUE_OPTIONS,
  },
  {
    kind: "toggle",
    key: "insider-free-shipping",
    label: "Insider free shipping",
    icon: "insider",
  },
  { kind: "toggle", key: "sale", label: "Sale" },
];

export interface FilterOption {
  value: string;
  label: string;
}

export interface CheckboxFilterOption extends FilterOption {
  /**
   * Renders a chevron beside the row, signalling the option drills into
   * narrower choices (e.g. European Union → member countries).
   */
  expandable?: boolean;
}

interface FilterSectionBase {
  key: string;
  title: string;
  /**
   * Sections with a chevron that collapses them. The three sections Figma
   * draws without one — brand minimum, ships from, wholesale price — are
   * always open.
   */
  collapsible?: boolean;
}

/** Single-choice section, e.g. Lead time. */
export interface RadioFilterSection extends FilterSectionBase {
  kind: "radio";
  options: FilterOption[];
}

/** Multi-choice section, e.g. Made in. */
export interface CheckboxFilterSection extends FilterSectionBase {
  kind: "checkbox";
  /** Renders a Slate Search above the options. */
  searchPlaceholder?: string;
  options: CheckboxFilterOption[];
  /** Options revealed by "Show more". */
  moreOptions?: CheckboxFilterOption[];
}

/**
 * Multi-choice section rendered as toggle pills rather than checkboxes, with
 * an "Another location" affordance that opens an inline search for the rest.
 */
export interface PillFilterSection extends FilterSectionBase {
  kind: "pill";
  /** Pills shown before anything is added. */
  options: FilterOption[];
  /** Label on the button that reveals the inline search. */
  addLabel: string;
  addPlaceholder: string;
  /** Everything the inline search can add; picks persist as pills. */
  searchableOptions: FilterOption[];
}

/** Category navigation section — reads as links rather than form controls. */
export interface LinkFilterSection extends FilterSectionBase {
  kind: "link";
  options: FilterOption[];
  moreOptions?: FilterOption[];
  /** The currently selected category, rendered emphasized and underlined. */
  activeValue: string;
}

/**
 * Bars drawn above a range slider, showing where the results actually sit.
 *
 * There is deliberately no bucket count: one bar per step, so every position
 * the knob can stop at is a boundary between two bars. Any other count leaves
 * the knob parked partway across a bar, which reads as a misaligned handle.
 */
export interface HistogramConfig {
  /** Which product value the bars count. */
  source: "price" | "brandMinimum";
  /** Height of the tallest bar, matching the Figma frame. */
  heightPx: number;
}

/** Money range, rendered as a histogram, a slider, and a centered read-out. */
export interface RangeFilterSection extends FilterSectionBase {
  kind: "range";
  rangeMin: number;
  rangeMax: number;
  step: number;
  /**
   * Pins the low end and reads as "$X or less". Used where only the ceiling
   * is meaningful, e.g. a brand's order minimum.
   */
  singleKnob?: boolean;
  histogram?: HistogramConfig;
  /**
   * A one-tap shortcut offered above the slider. Shares its key with the
   * Recommended pills, so the two move together.
   */
  quickToggle?: { key: string; label: string };
}

export type FilterSection =
  | RadioFilterSection
  | CheckboxFilterSection
  | PillFilterSection
  | LinkFilterSection
  | RangeFilterSection;

/** Countries the Made in search can add, beyond the three default pills. */
const MADE_IN_SEARCHABLE: FilterOption[] = [
  { value: "made-in:ca", label: "Canada" },
  { value: "made-in:mx", label: "Mexico" },
  { value: "made-in:jp", label: "Japan" },
  { value: "made-in:in", label: "India" },
  { value: "made-in:it", label: "Italy" },
  { value: "made-in:fr", label: "France" },
  { value: "made-in:pt", label: "Portugal" },
  { value: "made-in:au", label: "Australia" },
  { value: "made-in:br", label: "Brazil" },
  { value: "made-in:kr", label: "South Korea" },
];

/*
 * Brand location mixes states and countries — the design's own default pills
 * are California and United States — so the search offers both.
 */
const BRAND_LOCATION_SEARCHABLE: FilterOption[] = [
  { value: "brand-location:ny", label: "New York" },
  { value: "brand-location:tx", label: "Texas" },
  { value: "brand-location:il", label: "Illinois" },
  { value: "brand-location:wa", label: "Washington" },
  { value: "brand-location:co", label: "Colorado" },
  { value: "brand-location:fl", label: "Florida" },
  { value: "brand-location:eu", label: "European Union" },
  { value: "brand-location:uk", label: "United Kingdom" },
  { value: "brand-location:ca-country", label: "Canada" },
  { value: "brand-location:au", label: "Australia" },
  { value: "brand-location:jp", label: "Japan" },
];

/** Countries the Ships from search can add, beyond the two default pills. */
const SHIPS_FROM_SEARCHABLE: FilterOption[] = [
  { value: "ships-from:uk", label: "United Kingdom" },
  { value: "ships-from:ca", label: "Canada" },
  { value: "ships-from:mx", label: "Mexico" },
  { value: "ships-from:au", label: "Australia" },
  { value: "ships-from:nz", label: "New Zealand" },
  { value: "ships-from:jp", label: "Japan" },
  { value: "ships-from:kr", label: "South Korea" },
  { value: "ships-from:in", label: "India" },
  { value: "ships-from:br", label: "Brazil" },
  { value: "ships-from:za", label: "South Africa" },
];

/**
 * Section list, order, and copy taken from the Figma filters modal
 * (file EVLmXDKcBUfoDyzxqQj7yJ, node 3106:74164).
 */
export const FILTER_SECTIONS: FilterSection[] = [
  {
    kind: "range",
    key: "brand-minimum",
    title: "Brand minimum",
    collapsible: false,
    rangeMin: BRAND_MINIMUM_RANGE_MIN,
    rangeMax: BRAND_MINIMUM_RANGE_MAX,
    step: BRAND_MINIMUM_STEP,
    singleKnob: true,
    histogram: { source: "brandMinimum", heightPx: 73 },
    quickToggle: { key: "low-minimum", label: "Low minimum" },
  },
  {
    kind: "pill",
    key: "ships-from",
    title: "Ships from",
    collapsible: false,
    options: [
      { value: "ships-from:us", label: "United States (no import duties)" },
      { value: "ships-from:eu", label: "European Union" },
    ],
    addLabel: "Add location",
    addPlaceholder: "Search country or region",
    searchableOptions: SHIPS_FROM_SEARCHABLE,
  },
  {
    kind: "link",
    key: "category",
    title: "Category",
    activeValue: "all",
    options: [
      { value: "all", label: "All categories" },
      { value: "food-drink", label: "Food & drink" },
      { value: "paper-novelty", label: "Paper & novelty" },
      { value: "home-decor", label: "Home decor" },
    ],
    moreOptions: [
      { value: "jewelry", label: "Jewelry" },
      { value: "apparel", label: "Apparel & accessories" },
      { value: "beauty", label: "Beauty & wellness" },
      { value: "kids", label: "Baby & kids" },
    ],
  },
  /*
   * Shares the chip row's price state — "Wholesale price" and the Price chip
   * are the same filter, so editing either moves the other.
   */
  {
    kind: "range",
    key: "wholesale-price",
    title: "Wholesale price",
    collapsible: false,
    rangeMin: PRICE_RANGE_MIN,
    rangeMax: PRICE_RANGE_MAX,
    step: PRICE_STEP,
    histogram: { source: "price", heightPx: 82 },
  },
  {
    kind: "pill",
    key: "made-in",
    title: "Made in",
    collapsible: false,
    options: [
      { value: "made-in:us", label: "United States" },
      { value: "made-in:eu", label: "European Union" },
      { value: "made-in:uk", label: "United Kingdom" },
    ],
    addLabel: "Add location",
    addPlaceholder: "Search country or region",
    searchableOptions: MADE_IN_SEARCHABLE,
  },
  {
    kind: "pill",
    key: "brand-location",
    title: "Brand location",
    collapsible: false,
    options: [
      { value: "brand-location:california", label: "California" },
      { value: "brand-location:us", label: "United States" },
    ],
    addLabel: "Add location",
    addPlaceholder: "Search country or region",
    searchableOptions: BRAND_LOCATION_SEARCHABLE,
  },
  /*
   * Unprefixed values on purpose: these are the same values the Brand values
   * chip applies, so both controls read and write one selection.
   */
  {
    kind: "checkbox",
    key: "brand-values",
    title: "Brand values",
    options: BRAND_VALUE_OPTIONS.slice(0, VISIBLE_BRAND_VALUE_COUNT),
    moreOptions: BRAND_VALUE_OPTIONS.slice(VISIBLE_BRAND_VALUE_COUNT),
  },
  {
    kind: "radio",
    key: "lead-time",
    title: "Lead time",
    options: [
      { value: "3-days", label: "3 days or less" },
      { value: "6-days", label: "6 days or less" },
      { value: "9-days", label: "9 days or less" },
      { value: "14-days", label: "14 days or less" },
    ],
  },
  {
    kind: "checkbox",
    key: "product-types",
    title: "Product types",
    searchPlaceholder: "Search",
    options: [
      { value: "product-type:1", label: "Product type 1" },
      { value: "product-type:2", label: "Product type 2" },
      { value: "product-type:3", label: "Product type 3" },
    ],
    moreOptions: [
      { value: "product-type:4", label: "Product type 4" },
      { value: "product-type:5", label: "Product type 5" },
      { value: "product-type:6", label: "Product type 6" },
    ],
  },
  {
    kind: "checkbox",
    key: "brands",
    title: "Brands",
    searchPlaceholder: "Search",
    options: [
      { value: "brand:1", label: "Brand 1" },
      { value: "brand:2", label: "Brand 2" },
      { value: "brand:3", label: "Brand 3" },
    ],
    moreOptions: [
      { value: "brand:4", label: "Brand 4" },
      { value: "brand:5", label: "Brand 5" },
      { value: "brand:6", label: "Brand 6" },
    ],
  },
  {
    kind: "checkbox",
    key: "global-trade-item-number",
    title: "Global trade item number",
    options: [{ value: "gtin:includes", label: "Includes GTIN" }],
  },
];

/** A pill in the modal's "Recommended" section. */
export interface RecommendedFilter {
  key: string;
  label: string;
  /** Pills that carry a leading glyph in production. */
  icon?: "insider" | "topShop";
}

/**
 * Recommended filters, in the order the design lists them. Keys are shared
 * with `SUGGESTED_FILTERS` wherever the two surfaces offer the same filter, so
 * toggling one moves the other rather than holding a second, rival state.
 */
export const RECOMMENDED_FILTERS: RecommendedFilter[] = [
  { key: "low-minimum", label: "Low minimum" },
  { key: "california-brands", label: "California brands" },
  { key: "no-import-duties", label: "No import duties" },
  { key: "bestsellers", label: "Bestsellers" },
  {
    key: "insider-free-shipping",
    label: "Insider Free Shipping",
    icon: "insider",
  },
  { key: "top-shop", label: "Top Shop", icon: "topShop" },
  { key: "sale", label: "Sale" },
  { key: "hide-previously-seen", label: "Hide previously seen" },
];

/** Keys offered in the chip row too, so they are not counted twice. */
export const SUGGESTED_TOGGLE_KEYS = new Set(
  SUGGESTED_FILTERS.filter((filter) => filter.kind === "toggle").map(
    (filter) => filter.key
  )
);

/**
 * Checkbox values that a suggested chip already owns. They live in the same
 * flat `checkedValues` set as every other checkbox, so they must be excluded
 * from the "modal pick" chips — otherwise each one renders twice, once as its
 * own chip and once inside the Brand values dropdown label.
 */
export const CHIP_OWNED_CHECKBOX_VALUES = new Set(
  BRAND_VALUE_OPTIONS.map((option) => option.value)
);

/** Bounds for the leading match segment, per the prototype brief. */
export const MIN_TOP_MATCHES = 8;
export const MAX_TOP_MATCHES = 30;

/** Simulated search latency so the reflow reads as a fetch, not a jump. */
export const REFLOW_DELAY_MS = 400;

/**
 * A filter chosen inside the All filters modal, surfaced back into the chip
 * row. Radio picks are keyed `<sectionKey>=<value>`; checkbox picks are keyed
 * by value alone, matching how the modal reports them.
 */
export interface ModalSelection {
  /** The key the page stores, and the key used to remove it again. */
  key: string;
  label: string;
}

/*
 * Every value a section can put into `checkedValues`, whatever control it is
 * drawn as — checkbox rows and Ships from's pills both land in the same set.
 */
const valueEntries = FILTER_SECTIONS.flatMap((section) => {
  const options =
    section.kind === "checkbox"
      ? [...section.options, ...(section.moreOptions ?? [])]
      : section.kind === "pill"
        ? [...section.options, ...section.searchableOptions]
        : [];

  return options.map((option) => ({
    value: option.value,
    label: option.label,
    sectionTitle: section.title,
  }));
});

/*
 * "United States" is both a Made in and a Brand location option, so an
 * unqualified chip for either reads identically. Labels that occur in more
 * than one section are prefixed with the section they came from; unique ones
 * are left alone.
 */
const duplicatedLabels = new Set(
  valueEntries
    .map((entry) => entry.label)
    .filter(
      (label, index, labels) =>
        labels.indexOf(label) !== index || labels.lastIndexOf(label) !== index
    )
);

const checkboxOptionLabels = new Map<string, string>(
  valueEntries.map(
    (entry) =>
      [
        entry.value,
        duplicatedLabels.has(entry.label)
          ? `${entry.sectionTitle}: ${entry.label}`
          : entry.label,
      ] as const
  )
);

const radioOptionLabels = new Map<string, string>(
  FILTER_SECTIONS.filter(
    (section): section is RadioFilterSection => section.kind === "radio"
  ).flatMap((section) =>
    section.options.map(
      (option) => [`${section.key}=${option.value}`, option.label] as const
    )
  )
);

/**
 * Chip label for a modal selection. Falls back to the raw key so an option
 * added to a section without a label still renders something clickable.
 */
export const labelForModalSelection = (key: string): string =>
  radioOptionLabels.get(key) ?? checkboxOptionLabels.get(key) ?? key;
